import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import DecimalField from '../shared/DecimalField';
import { DAMAGE_TYPES } from '../../utils/damageTypes';

const dateStringToDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00');
};

const dateToDateString = (date) => {
  if (!date || isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function EditInvoiceInsuranceDialog({ open, handleClose, invoice, onUpdate, setSnackbar, currentOrganization }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  useEffect(() => {
    if (invoice && open) {
      setData({
        insurance_claim_number: invoice.insurance_claim_number || '',
        deductible_amount: invoice.deductible_amount ?? '',
        insurance_damage_date: invoice.insurance_damage_date || null,
        insurance_damage_type: invoice.insurance_damage_type || '',
        insurance_inspector_name: invoice.insurance_inspector_name || '',
        insurance_inspector_code: invoice.insurance_inspector_code || '',
        insurance_inspection_date: invoice.insurance_inspection_date || null,
        repair_start_date: invoice.repair_start_date || null,
        repair_end_date: invoice.repair_end_date || null,
        policyholder_info: invoice.policyholder_info || '',
        seller_site_code: invoice.seller_site_code || currentOrganization?.insurance_seller_site_code || '',
        invoice_split_type: invoice.invoice_split_type || 'normal',
      });
    }
  }, [invoice, open, currentOrganization]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        insurance_claim_number: data.insurance_claim_number,
        deductible_amount: data.deductible_amount || null,
        insurance_damage_date: data.insurance_damage_date || null,
        insurance_damage_type: data.insurance_damage_type,
        insurance_inspector_name: data.insurance_inspector_name,
        insurance_inspector_code: data.insurance_inspector_code,
        insurance_inspection_date: data.insurance_inspection_date || null,
        repair_start_date: data.repair_start_date || null,
        repair_end_date: data.repair_end_date || null,
        policyholder_info: data.policyholder_info,
        seller_site_code: data.seller_site_code,
        invoice_split_type: data.invoice_split_type,
      };
      const res = await axios.patch(`/api/invoices/${invoice.id}/update_insurance_details/`, payload);
      onUpdate(res.data);
      handleClose();
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || t('Save failed');
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: '18px' }}>
          {t('Insurance details')}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {/* Split type */}
          <Grid item xs={12}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('Invoice category')}</InputLabel>
              <Select
                value={data.invoice_split_type || 'normal'}
                label={t('Invoice category')}
                onChange={(e) => setData(prev => ({ ...prev, invoice_split_type: e.target.value }))}
              >
                <MenuItem value="normal">{t('splitType.normal')}</MenuItem>
                <MenuItem value="insurance">{t('splitType.insurance')}</MenuItem>
                <MenuItem value="deductible">{t('splitType.deductible')}</MenuItem>
                <MenuItem value="vat_company">{t('splitType.vat_company')}</MenuItem>
                <MenuItem value="billing_customer">{t('splitType.billing_customer')}</MenuItem>
                <MenuItem value="customer_share">{t('splitType.customer_share')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Core fields */}
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              label={t('Insurance claim number')}
              value={data.insurance_claim_number || ''}
              onChange={(e) => setData(prev => ({ ...prev, insurance_claim_number: e.target.value }))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DecimalField
              size="small"
              label={t('Deductible amount') + ' (€)'}
              value={data.deductible_amount ?? ''}
              onChange={(val) => setData(prev => ({ ...prev, deductible_amount: val }))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
              <DatePicker
                label={t('Damage date')}
                value={dateStringToDate(data.insurance_damage_date)}
                onChange={(newValue) => {
                  const newDateStr = dateToDateString(newValue) || null;
                  if (newValue && !newDateStr) return;
                  setData(prev => ({ ...prev, insurance_damage_date: newDateStr }));
                }}
                format="dd.MM.yyyy"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('Damage type')}</InputLabel>
              <Select
                value={data.insurance_damage_type || ''}
                label={t('Damage type')}
                onChange={(e) => setData(prev => ({ ...prev, insurance_damage_type: e.target.value }))}
              >
                <MenuItem value="">{t('Not selected')}</MenuItem>
                {DAMAGE_TYPES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`damageType.${value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Inspector details */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 1 }}>
              {t('Inspector details')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              label={t('Inspector name')}
              value={data.insurance_inspector_name || ''}
              onChange={(e) => setData(prev => ({ ...prev, insurance_inspector_name: e.target.value }))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              label={t('Inspector code')}
              value={data.insurance_inspector_code || ''}
              onChange={(e) => setData(prev => ({ ...prev, insurance_inspector_code: e.target.value }))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
              <DatePicker
                label={t('Inspection date')}
                value={dateStringToDate(data.insurance_inspection_date)}
                onChange={(newValue) => {
                  const newDateStr = dateToDateString(newValue) || null;
                  if (newValue && !newDateStr) return;
                  setData(prev => ({ ...prev, insurance_inspection_date: newDateStr }));
                }}
                format="dd.MM.yyyy"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Repair dates */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 1 }}>
              {t('Repair dates')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
              <DatePicker
                label={t('Repair start date')}
                value={dateStringToDate(data.repair_start_date)}
                onChange={(newValue) => {
                  const newDateStr = dateToDateString(newValue) || null;
                  if (newValue && !newDateStr) return;
                  setData(prev => ({ ...prev, repair_start_date: newDateStr }));
                }}
                format="dd.MM.yyyy"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
              <DatePicker
                label={t('Repair end date')}
                value={dateStringToDate(data.repair_end_date)}
                onChange={(newValue) => {
                  const newDateStr = dateToDateString(newValue) || null;
                  if (newValue && !newDateStr) return;
                  setData(prev => ({ ...prev, repair_end_date: newDateStr }));
                }}
                format="dd.MM.yyyy"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Additional fields */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 1 }}>
              {t('Additional info')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              size="small"
              label={t('Policyholder info')}
              value={data.policyholder_info || ''}
              onChange={(e) => setData(prev => ({ ...prev, policyholder_info: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
              helperText={t('policyholderInfoHelp')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              label={t('Seller site code')}
              value={data.seller_site_code || ''}
              onChange={(e) => setData(prev => ({ ...prev, seller_site_code: e.target.value }))}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          sx={{ borderRadius: '8px', px: 2.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          sx={{
            bgcolor: 'secondary.main',
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditInvoiceInsuranceDialog;
